import React, { useState, useEffect } from "reactn";
import { useHistory } from "react-router-dom";
import PropTypes from "prop-types";
import {
    EditableGridWrapper,
    EditableGridItem,
    EditableGridBtns,
    EditableGridBtn,
    EditableGridInfo,
    EditableGridCont,
    EditableGridLink,
    PagingSelect,
    DeletePrompt,
    AddressWrapper,
    AddressGridWrapper,
    EditableGridContBtns
} from "../../_common/form/editableGrid/EditableGrid.styled";

// components
import Pagination from "shared-anpost-components/components/form/pagination/Pagination";
import FormGroup from "shared-anpost-components/components/form/form_group/FormGroup";
import InputGroup from "shared-anpost-components/components/form/input_group/InputGroup";
import Select from "shared-anpost-components/components/form/select/Select";
import { FiltersWrapper, Filter } from "../../_common/form/filters/filters.styled";
import PageLink from "../../_common/link/PageLink";
import Loader from "shared-anpost-components/components/loader/Loader";
import GlobalError from "shared-anpost-components/components/globalError/GlobalError";
import Error from "../../_common/error/Error";
import PageTitle from "shared-anpost-components/components/pageTitle/PageTitle.styled";

// hooks
import profileHooks from "../../../hooks/profileHooks";
import addressHooks from "../../../hooks/addressHooks";

// apis
import { deleteAddress, deleteFavourite, getJWTInfo, getProfile } from "../../../services/Api";

// helpers
import { virtualPageView, capitaliseFirstLetters } from "../../../helpers/Helpers";

const AddressesGrid = props => {
    const [loader, setLoader] = useState(false);
    const [apiError, setApiError] = useState(false);

    const { items, deleteBtn, editBtn, newItem, filters, filterActive, onAddressUpdate } = props;

    const { values } = profileHooks();
    const { setContactValues, setAdditionalAddressVals, resetPersonalAddressData, resetContactAddressData, updateAddressObject, clearAddress } = addressHooks();

    const history = useHistory();
    /**
     * Sets current page
     */
    const [currentPage, setCurrentPage] = useState(0);

    /**
     * Sets current limit
     */
    const [currentLimit, setCurrentLimit] = useState(999);

    /**
     * Sets items per page
     */
    const [itemsPerPage, setItemsPerPage] = useState(999);

    /**
     * Sets searched Items
     */
    const [filteredItems, setFilteredItems] = useState(items);

    /**
     * Set Active Filter
     */
    const [activeFilter, setActiveFilter] = useState(filterActive);

    /**
     * Set Filters List
     */
    const [filtersList, setFiltersList] = useState(filters);

    /**
     * Set Delete prompt
     */
    const [deletePrompt, setDeletePrompt] = useState("");

    /**
     * Get the number per page
     */

    const itemLength = items.length;
    const numbOfItems = Math.round(itemLength / 999);
    const ordersPerPage = [];
    for (let i = 0; i < numbOfItems; i += 1) {
        ordersPerPage.push({
            id: (i + 1) * 999,
            available: true,
            value: (i + 1) * 999
        });
    }

    /**
     * Order per page change
     */
    const ordersPerPageChange = e => {
        setItemsPerPage(parseInt(e.target.value, 10));
    };

    const handleAnalytics = id => {
        let virtualUrl = "all-addresses";
        let virtualTitle = "All addresses";

        if (id === "personalAddresses") {
            virtualUrl = "my-addresses";
            virtualTitle = "My addresses";
        } else if (id === "contactAddresses") {
            virtualUrl = "my-contacts";
            virtualTitle = "My Contacts";
        }

        virtualPageView(`/myAccount/Addresses/${virtualUrl}`, `My Account - ${virtualTitle}`);
    };

    const updateCurrentFilter = id => {
        setActiveFilter(id);
        setCurrentPage(0);

        handleAnalytics(id);
    };

    const closeFunc = () => {
        setApiError(false);
    };

    /**
     * Loop through filters, only show filters that exist in items array
     */
    const checkFilterExist = filter => {
        // "all" filter returns always
        let filterExists = filter.id === "all";
        for (let i = 0; i < items.length; i += 1) {
            if (items[i].filter && items[i].filter === filter.id) {
                filterExists = true;
            }
        }
        return filterExists;
    };

    /**
     * Show edit contact
     */
    const showEditPersonal = address => {
        updateAddressObject("personalAddress", address);

        setAdditionalAddressVals("personalAddress", address, values);
        if (address.displayName === "Home" || address.displayName === "Business") {
            history.push("/Addresses/edit-address");
        } else {
            history.push("/Addresses/edit-secondary-address");
        }
    };

    const handleError = () => {
        setLoader(false);
        setApiError(true);
    };

    /**
     * Show edit contact
     */
    const showEditContact = contact => {
        setContactValues(contact);

        updateAddressObject("contactAddress", contact.receiverAddress);

        history.push("/Addresses/edit-contact");
    };

    const showEditForm = item => {
        if (item.filter === "contactAddresses") {
            showEditContact(item);
        } else if (item.filter === "personalAddresses") {
            showEditPersonal(item);
        }
    };

    const addNewAddressClick = () => {
        resetPersonalAddressData(items.length === 0);

        clearAddress("personalAddress");

        history.push("/Addresses/add-new-address");
    };

    const addNewContactClick = () => {
        resetContactAddressData();

        clearAddress("contactAddress");

        history.push("/Addresses/add-new-contact");
    };

    const deleteContactAddress = addressId => {
        // Data that is passed to API
        const addressData = {
            receiverId: addressId,
            ciamCustomerId: values.ciamUserId,
            sessionId: `${window.cmsSessionId}`
        };

        // Call Api
        getJWTInfo().then(() => {
            deleteFavourite(addressData)
                .then(res => {
                    if (res.status === 200) {
						onAddressUpdate();
                    } else {
                        handleError();
                    }
                })
                .catch(() => {
                    handleError();
                });
        });
    };

    const deletePersonalAddress = item => {
        setLoader(true);
        const addressData = {
            UID: values.ciamUserId,
            id: item.id
        };
        getJWTInfo().then(() => {
            deleteAddress(addressData)
                .then(res => {
                    if (res.status === 200) {
						setLoader(false);

                        getProfile(values.ciamUserId).then(res => {
                            if (res.status === 200) {
                                sessionStorage.setItem("userProfile", JSON.stringify(res.data));
                                onAddressUpdate();
                            };
                        });
                    } else {
                        handleError();
                    }
                })
                .catch(() => {
                    handleError();
                });
        });
    };

    const deleteItem = (e, item, addressType, addressId) => {
        e.preventDefault();

        if (addressType === "personal") {
            deletePersonalAddress(item);
        }

        if (addressType === "contact") {
            deleteContactAddress(addressId);
        }
    };

    useEffect(() => {
        if (currentPage + itemsPerPage > items.length) {
            // if limit is less than 999 - set the limit to 999
            setCurrentLimit(filteredItems.length > 999 ? filteredItems.length : 999);
        } else {
            setCurrentLimit(currentPage + itemsPerPage);
        }

        // set the filter to all results
        if (activeFilter === "all") {
            setFilteredItems(items);
        } else {
            // filter by the selected filter
            const filterItems = items.filter(item => {
                return item.filter && item.filter === activeFilter;
            });
            setFilteredItems(filterItems);
        }

        // get a list of the filters that exist in the array of items
        if (filters) {
            const filteredFilterList = filters.filter(item => {
                return checkFilterExist(item);
            });

            setFiltersList(filteredFilterList);
        }
    }, [items, currentPage, itemsPerPage, activeFilter]);

    useEffect(() => {
        clearAddress("personalAddress");
    }, []);

    return (
        <>
            {loader && <Loader />}
            {apiError && <GlobalError closeFunc={closeFunc} />}
            <EditableGridWrapper>
                <>
                    <h3 className="h4">Address book</h3>
                    <h4 className="h6">Manage your addresses and contacts</h4>
                    <p>Your profile addresses should belong to you. These are used as the sender address when buying labels online. Your contacts are the people who you wish to send items to.</p>
                    <EditableGridCont>
                        {filters && values.loggedIn && (
                            <FiltersWrapper>
                                {filtersList.map(item => {
                                    return (
                                        <Filter
                                            key={item.id}
                                            onClick={e => {
                                                e.preventDefault();
                                                updateCurrentFilter(item.id);
                                            }}
                                            active={activeFilter === item.id}
                                            aria-label={`Filter by ${item.label}`}
                                        >
                                            {item.label}
                                        </Filter>
                                    );
                                })}
                            </FiltersWrapper>
                        )}
                    </EditableGridCont>
                    {items.length === 0 && values.loggedIn && <h5>There are no addresses connected to your profile yet. You can add them below.</h5>}
                    {!values.loggedIn && <Error errorHeading="You are not logged in. You need to be logged into your An Post account to view this page." loginPrompt></Error>}
                    <AddressGridWrapper>
                        {filteredItems.slice(currentPage, currentLimit).map(item => {
                            let person;
                            let address;
                            let addressId;
                            let addressType;
                            if (item.receiver) {
                                person = item.receiver;
                                address = item.receiverAddress;
                                addressId = item.receiverId;
                                addressType = "contact";
                            } else {
                                address = item;
                                addressId = item.id;
                                addressType = "personal";
                            }
                            return (
                                <EditableGridItem addresses key={addressId}>
                                    <>
                                        <EditableGridInfo type={addressType}>
                                            {addressType === "contact" && (
                                                <>
                                                    {person && person.firstName && (
                                                        <h5 className="h6">
                                                            {person.firstName} {person.lastName}
                                                        </h5>
                                                    )}
                                                </>
                                            )}
                                            {addressType === "personal" && <>{address && address.displayName && <h5 className="h6">{address.displayName}</h5>}</>}
                                            {address && (
                                                <AddressWrapper>
                                                    {address.organisationName && <p>{capitaliseFirstLetters(address.organisationName)},</p>}
                                                    {address.addressLine1 && <p>{capitaliseFirstLetters(address.addressLine1)},</p>}
                                                    {address.addressLine2 && <p>{capitaliseFirstLetters(address.addressLine2)},</p>}
                                                    {address.addressLine3 && <p>{capitaliseFirstLetters(address.addressLine3)},</p>}
                                                    {address.addressLine4 && <p>{capitaliseFirstLetters(address.addressLine4)},</p>}
                                                    {address.town && <p>{capitaliseFirstLetters(address.town)},</p>}
                                                    {address.city && <p>{capitaliseFirstLetters(address.city)},</p>}
                                                    {address.county && <p>{capitaliseFirstLetters(address.county)},</p>}
                                                    {address.eircode && address.country && address.country.toUpperCase() === "IRELAND" && <p>{address.eircode.toUpperCase()},</p>}
                                                    {address.postcode && address.country && address.country.toUpperCase() !== "IRELAND" && <p>{address.postcode.toUpperCase()},</p>}
                                                    {address.country && <p>{capitaliseFirstLetters(address.country)}</p>}
                                                </AddressWrapper>
                                            )}
                                            {address.defaultAddress && <p className="deliveryAddress">Default delivery address</p>}
                                        </EditableGridInfo>
                                        <EditableGridBtns addresses>
                                            {deletePrompt === addressId && (
                                                <>
                                                    <DeletePrompt addresses>
                                                        <span className="icon icon-warning"></span>
                                                        <p>This cannot be undone.</p>
                                                        <p>Are you sure you want to delete this address from your profile?</p>
                                                        <p>
                                                            <PageLink
                                                                text="Keep this address"
                                                                className="m84__basket__item__delete__reject"
                                                                onClick={e => {
                                                                    e.preventDefault();
                                                                    setDeletePrompt("");
                                                                }}
                                                            ></PageLink>
                                                        </p>
                                                        <p>
                                                            <PageLink
                                                                text="Delete this address"
                                                                className="m84__basket__item__delete__confirm"
                                                                onClick={e => {
                                                                    deleteItem(e, address, addressType, addressId);
                                                                }}
                                                            ></PageLink>
                                                        </p>
                                                    </DeletePrompt>
                                                </>
                                            )}
                                            {deleteBtn && (deletePrompt !== addressId && (address.displayName !== "Home" && address.displayName !== "Business" && !address.defaultAddress)) && (
                                                <EditableGridBtn
                                                    addresses
                                                    delete
                                                    onClick={() => {
                                                        setDeletePrompt(addressId);
                                                    }}
                                                >
                                                    <p>Delete</p>
                                                    <span className="icon controls-delete"></span>
                                                </EditableGridBtn>
                                            )}
                                            {editBtn && deletePrompt !== addressId && (
                                                <EditableGridBtn
                                                    addresses
                                                    edit
                                                    onClick={() => {
                                                        showEditForm(item);
                                                    }}
                                                >
                                                    <p>Edit</p>
                                                    <span className="icon edit-button"></span>
                                                </EditableGridBtn>
                                            )}
                                        </EditableGridBtns>
                                    </>
                                </EditableGridItem>
                            );
                        })}
                    </AddressGridWrapper>
                </>
                {newItem && values.loggedIn && (
                    <EditableGridContBtns>
                        <div>
                            {(activeFilter === "all" || activeFilter === "personalAddresses") && (
                                <EditableGridLink
                                    personal
                                    className="bn bn--secondary gtm-cta"
                                    onClick={() => {
                                        addNewAddressClick();
                                    }}
                                >
                                    Add a new address
                                </EditableGridLink>
                            )}
                            {(activeFilter === "all" || activeFilter === "contactAddresses") && (
                                <EditableGridLink
                                    contact
                                    className="bn bn--secondary gtm-cta"
                                    onClick={() => {
                                        addNewContactClick();
                                    }}
                                >
                                    Add a new contact
                                </EditableGridLink>
                            )}
                        </div>
                    </EditableGridContBtns>
                )}
                {filteredItems.length > 999 && (
                    <EditableGridCont>
                        <PagingSelect>
                            <FormGroup focused>
                                <InputGroup>
                                    <Select
                                        id="ordersperpage"
                                        type="number"
                                        name="ordersperpage"
                                        change={ordersPerPageChange}
                                        labelText="Items per page"
                                        defaultValue="Items per Page"
                                        options={ordersPerPage}
                                        required
                                    />
                                </InputGroup>
                            </FormGroup>
                        </PagingSelect>
                        <Pagination pageCount={filteredItems.length} pageSize={itemsPerPage} pageChange={setCurrentPage} />
                    </EditableGridCont>
                )}
            </EditableGridWrapper>
        </>
    );
};

export default AddressesGrid;

AddressesGrid.propTypes = {
    items: PropTypes.array,
    deleteBtn: PropTypes.bool,
    editBtn: PropTypes.bool,
    newItem: PropTypes.bool,
    filters: PropTypes.array,
    filterActive: PropTypes.string,
	onAddressUpdate: PropTypes.func
};
